import { MEMORY_TEXT_MAX_CHARS, type MemoryProposalView } from "@esi/shared";
import { BookmarkCheck, BookmarkPlus, FileText, TriangleAlert, Users, X } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { DismissReasons } from "@/components/DismissReasons";
import { InlineError } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { ApiError } from "../api-errors.ts";
import { ACCOUNT_PATH, Link, projectHomePath } from "../router.tsx";
import { useProposalDecision, useProposalSuggestion } from "./queries.ts";

/**
 * A memory the assistant suggests after an answer (openspec change `memory-personal`): the exact
 * text that would be saved, what it applies to, and Save, Edit and Dismiss. Nothing is saved
 * without the user's click. Dashed while undecided ("not yet"), like pending chips; a saved
 * proposal settles into a quiet confirmation line. In a project, a contributor may instead send
 * a fact to the project's memory (openspec change `memory-project`), where an owner or manager
 * reviews it; that consumes the proposal.
 */
/** How the caller decided a proposal, kept on the chat's part like a wiki suggestion's. */
export type MemoryProposalDecision = "saved" | "dismissed" | "suggested" | "added";

export function MemoryProposalCard(props: {
  proposal: MemoryProposalView;
  /** A decision taken earlier on this chat (the card was unmounted since). */
  decided?: MemoryProposalDecision | undefined;
  onDecided?: (decision: MemoryProposalDecision) => void;
  /** False once the next turn started: a dismissal no longer asks why. */
  askWhy?: boolean | undefined;
}) {
  const { proposal } = props;
  const { t } = useTranslation();
  const id = useId();
  const { accept, dismiss, reason } = useProposalDecision(proposal);
  const suggest = useProposalSuggestion(proposal);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(proposal.text);
  const [state, setDecided] = useState<"open" | MemoryProposalDecision>(props.decided ?? "open");
  // Decided before this card mounted (the chat keeps the decision): no reasons are offered.
  const [decidedEarlier] = useState(props.decided !== undefined);
  const setState = (decision: MemoryProposalDecision) => {
    setDecided(decision);
    props.onDecided?.(decision);
  };
  const error = accept.error ?? dismiss.error ?? suggest.error;
  const gone = error instanceof ApiError && error.status === 404;
  const busy = accept.isPending || dismiss.isPending || suggest.isPending;
  const suggestable = proposal.suggestable === true && proposal.origin !== null;
  // The assistant proposed it for the project (agent-turn D13): that action leads.
  const projectFirst = suggestable && proposal.toProject === true;
  const projectName = proposal.origin?.name ?? t("memory.proposal.thisProject");
  const trimmed = text.trim();
  const tooLong = trimmed.length > MEMORY_TEXT_MAX_CHARS;

  if (state === "dismissed") {
    // Dismissed here and now: why, optionally; an earlier decision only says it.
    return (
      <DismissReasons onReason={reason} offer={!decidedEarlier && props.askWhy !== false}>
        {t("memory.proposal.dismissed")}
      </DismissReasons>
    );
  }
  if (state === "saved") {
    return (
      <p
        role="status"
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted"
      >
        <BookmarkCheck aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        <span>{t("memory.proposal.saved")}</span>
        <Link
          href={`${ACCOUNT_PATH}#memory`}
          className="text-accent-text underline-offset-4 hover:underline"
        >
          {t("memory.proposal.manage")}
        </Link>
      </p>
    );
  }

  if ((state === "suggested" || state === "added") && proposal.origin) {
    return (
      <p
        role="status"
        className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted"
      >
        <Users aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
        <span>
          {state === "added"
            ? t("memory.proposal.addedProject", { project: projectName })
            : t("memory.proposal.suggestedProject", { project: projectName })}
        </span>
        <Link
          href={projectHomePath(proposal.origin.projectId, "memory")}
          className="text-accent-text underline-offset-4 hover:underline"
        >
          {t("memory.proposal.viewProject")}
        </Link>
      </p>
    );
  }

  const suggestButton = (variant: "default" | "ghost") => (
    <Button
      type="button"
      size="sm"
      variant={variant}
      disabled={busy}
      onClick={() =>
        suggest.mutate(undefined, {
          onSuccess: (view) => setState(view.status === "active" ? "added" : "suggested"),
        })
      }
    >
      <Users aria-hidden="true" strokeWidth={1.75} />
      {suggest.isPending ? t("memory.proposal.suggesting") : t("memory.proposal.suggestProject")}
    </Button>
  );
  const save = () =>
    accept.mutate(editing && trimmed !== proposal.text ? trimmed : undefined, {
      onSuccess: () => setState("saved"),
    });
  const scope =
    proposal.kind === "preference"
      ? t("memory.proposal.preference")
      : proposal.origin
        ? t("memory.proposal.fact", {
            project: proposal.origin.name ?? t("memory.proposal.thisProject"),
          })
        : t("memory.proposal.factStandalone");

  return (
    <section
      aria-labelledby={`${id}-title`}
      className={cn(
        "flex flex-col gap-2.5 rounded-lg border border-dashed border-line-strong px-3.5 py-3",
        "transition-colors duration-(--duration-fast) ease-(--ease-out) focus-within:border-solid",
      )}
    >
      <div className="flex items-start gap-2.5">
        <BookmarkPlus
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <h3 id={`${id}-title`} className="text-[13px] font-medium text-fg-secondary">
            {proposal.requested ? t("memory.proposal.requested") : t("memory.proposal.suggested")}
          </h3>
          {editing ? (
            <div className="flex flex-col gap-1">
              <label htmlFor={`${id}-text`} className="sr-only">
                {t("memory.proposal.textLabel")}
              </label>
              <Textarea
                id={`${id}-text`}
                value={text}
                rows={2}
                autoFocus
                aria-invalid={tooLong || undefined}
                aria-describedby={`${id}-count`}
                // Editing continues the text: the caret starts at its end.
                onFocus={(e) => {
                  const end = e.currentTarget.value.length;
                  e.currentTarget.setSelectionRange(end, end);
                }}
                onChange={(e) => {
                  accept.reset();
                  setText(e.currentTarget.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setEditing(false);
                    setText(proposal.text);
                  }
                }}
                className="min-h-0 resize-none text-sm"
              />
              <span
                id={`${id}-count`}
                className={cn("text-xs tabular-nums", tooLong ? "text-danger-fg" : "text-fg-muted")}
              >
                {t("memory.proposal.remaining", {
                  count: MEMORY_TEXT_MAX_CHARS - trimmed.length,
                })}
              </span>
            </div>
          ) : (
            <p className="text-sm leading-relaxed text-fg">{proposal.text}</p>
          )}
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-fg-muted">
            <span>{scope}</span>
            {proposal.sourceCount > 0 ? (
              <>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1">
                  <FileText aria-hidden="true" className="size-3" strokeWidth={1.75} />
                  {t("memory.proposal.sources", { count: proposal.sourceCount })}
                </span>
              </>
            ) : null}
            {proposal.trust === "external" ? (
              <>
                <span aria-hidden="true">·</span>
                <span className="inline-flex items-center gap-1 text-warning-fg">
                  <TriangleAlert aria-hidden="true" className="size-3" strokeWidth={1.75} />
                  {t("memory.proposal.unverified")}
                </span>
              </>
            ) : null}
          </p>
        </div>
      </div>
      {error ? (
        <InlineError>
          {gone
            ? t("memory.proposal.gone")
            : error instanceof ApiError && error.status === 422
              ? t("memory.proposal.invalid")
              : error instanceof ApiError && error.code === "memory_queue_full"
                ? t("memory.panel.queueFull")
                : error instanceof ApiError && error.code === "memory_duplicate"
                  ? t("memory.panel.duplicate")
                  : error instanceof ApiError
                    ? error.message
                    : t("errors.network")}
        </InlineError>
      ) : null}
      {gone ? null : (
        <div className="flex flex-wrap items-center gap-1.5 pl-6.5">
          {projectFirst && !editing ? suggestButton("default") : null}
          <Button
            type="button"
            size="sm"
            variant={projectFirst ? "outline" : "default"}
            disabled={busy || !trimmed || tooLong}
            onClick={save}
          >
            {accept.isPending
              ? t("memory.proposal.saving")
              : projectFirst
                ? t("memory.proposal.saveForMe")
                : t("memory.proposal.save")}
          </Button>
          {editing ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setEditing(false);
                setText(proposal.text);
                accept.reset();
              }}
            >
              {t("memory.proposal.cancel")}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => setEditing(true)}
            >
              {t("memory.proposal.edit")}
            </Button>
          )}
          {suggestable && !projectFirst && !editing ? suggestButton("ghost") : null}
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            className="text-fg-secondary"
            onClick={() => dismiss.mutate(undefined, { onSuccess: () => setState("dismissed") })}
          >
            <X aria-hidden="true" strokeWidth={1.75} />
            {t("memory.proposal.dismiss")}
          </Button>
        </div>
      )}
    </section>
  );
}
